/* metadoc search api for index status, indexing, and queries. */
import { api } from "./api";

export type MetadocSearchGeometry = {
  bounding_box: {
    left: number;
    top: number;
    width: number;
    height: number;
  };
  polygon: { x: number; y: number }[];
  confidence: number;
};

export type MetadocSearchResult = {
  metadoc_id: number;
  page_number: number;
  text: string;
  geometry: MetadocSearchGeometry;
  score: number;
  highlights: string[];
  version_suffix: string;
};

export type MetadocSearchResponse = {
  results: MetadocSearchResult[];
  total_hits: number;
  page: number;
  page_size: number;
  total_pages: number;
};

export type MetadocSearchIndexStatus = {
  metadoc_id: number;
  metadoc_name: string;
  status:
    | "pending"
    | "processing"
    | "completed"
    | "failed"
    | "invalidated"
    | "not_indexed";
  opensearch_document_id: string | null;
  total_pages_indexed: number | null;
  total_text_blocks_indexed: number | null;
  error_message: string | null;
  indexing_started_at: string | null;
  indexing_completed_at: string | null;
  is_searchable: boolean;
};

export type MetadocSearchIndexVersionStatus = Omit<
  MetadocSearchIndexStatus,
  "metadoc_id" | "metadoc_name" | "status"
> & {
  version_suffix: string;
  status: "pending" | "processing" | "completed" | "failed" | "invalidated";
};

type MetadocSearchIndexStatusResponse = {
  metadoc_id: number;
  metadoc_name: string;
  versions: MetadocSearchIndexVersionStatus[];
};

type SearchMetadocArgs = {
  metadocId: number;
  query: string;
  page?: number;
  pageSize?: number;
  pageNumber?: number;
  versionSuffix?: string;
};

type StartMetadocIndexArgs = {
  metadocId: number;
  versionSuffix: string;
  forceReindex?: boolean;
};

type StartMetadocIndexResponse = {
  message: string;
  task_id: string;
  metadoc_id: number;
  version_suffix: string;
};

type GetMetadocSearchIndexStatusArgs = {
  metadocId: number;
};

const metadocSearchApi = api.injectEndpoints({
  overrideExisting: true,
  endpoints: (build) => ({
    searchMetadoc: build.mutation<MetadocSearchResponse, SearchMetadocArgs>({
      query: ({
        metadocId,
        query,
        page = 1,
        pageSize = 20,
        pageNumber,
        versionSuffix,
      }) => ({
        url: "api/v1/metadoc-search/search/",
        method: "POST",
        body: {
          metadoc_id: metadocId,
          query,
          page,
          page_size: pageSize,
          ...(pageNumber ? { page_number: pageNumber } : {}),
          ...(versionSuffix !== undefined
            ? { version_suffix: versionSuffix }
            : {}),
        },
      }),
    }),
    startMetadocSearchIndex: build.mutation<
      StartMetadocIndexResponse,
      StartMetadocIndexArgs
    >({
      // Only the version currently being viewed is searchable, so indexing the
      // other one would pay for Textract nobody queries.
      query: ({ metadocId, versionSuffix, forceReindex = false }) => ({
        url: "api/v1/metadoc-search/index-version/",
        method: "POST",
        body: {
          metadoc_id: metadocId,
          version_suffix: versionSuffix,
          force_reindex: forceReindex,
        },
      }),
    }),
    getMetadocSearchIndexStatus: build.query<
      MetadocSearchIndexStatusResponse,
      GetMetadocSearchIndexStatusArgs
    >({
      query: ({ metadocId }) => ({
        url: `api/v1/metadoc-search/${metadocId}/index-status/`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useSearchMetadocMutation,
  useStartMetadocSearchIndexMutation,
  useGetMetadocSearchIndexStatusQuery,
} = metadocSearchApi;
